import { fn, get } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import RoomHierarchyColumn from 'lumen/components/settings/recommendations/room-hierarchy/column-room-hierarchy';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  RoomOffsetHierarchy,
  RoomOffsetHierarchySettings,
} from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  findPrimaryPath,
  resolveOffsetValue,
  updateHierarchy,
} from 'lumen/utils/pricing-assistant/room-hierarchy-settings';

import styles from './table-room-hierarchy.module.css';

type roomHierarchyByLevel = {
  roomCode: string;
  roomHierarchy: RoomOffsetHierarchy;
}[][];

interface SettingsRecommendationsRoomHierarchyTableRoomHierarchySignature {
  Args: {
    isEditMode: boolean;
    onOffsetRangeErrorChange: (hasError: boolean) => void;
    strategy: RecommendationStrategy;
  };
}

function collectAllRoomCodes(
  completeRoomHierarchy: Record<string, RoomOffsetHierarchy>,
): string[] {
  return Object.entries(completeRoomHierarchy).flatMap(
    ([roomCode, hierarchy]) => [
      roomCode,
      ...collectAllRoomCodes(hierarchy.children),
    ],
  );
}

// Builds the default selectionRoomCodePath (1-based) from a hierarchy's primary chain.
function buildDefaultSelectionPath(
  hierarchy: Record<string, RoomOffsetHierarchy>,
  leadingRoomCode: string | null,
): string[] {
  return ['', ...findPrimaryPath(hierarchy, leadingRoomCode)];
}

export default class SettingsRecommendationsRoomHierarchyTable extends Component<SettingsRecommendationsRoomHierarchyTableRoomHierarchySignature> {
  @service declare featureToggle: Services['feature-toggle'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked editingRoomCode: string | null = null;

  /**
   * Marks the deepest level the user has explicitly clicked into.
   * Initial state is 0, making that condition true for every level →
   * entire path is auto-expanded and siblings hidden at every level.
   */
  @tracked lastActivatedLevel = 0;

  @tracked pendingChildParentCode: string | null = null;
  @tracked selectedRoomCode: string | null = null;

  /**
   * Ancestor path of selected room codes that determines which children are visible.
   * 1-based, mirroring roomHierarchyByLevel: selectionRoomCodePath[level] = the room the user clicked at that level.
   */
  @tracked selectionRoomCodePath: string[] = [];

  // CSS keeps scrollLeft when overflow-x switches back to hidden.
  resetHorizontalScroll = modifier(
    (element: HTMLElement, [roomsByLevel]: [roomHierarchyByLevel]): void => {
      const isLargeViewport = window.matchMedia('(min-width: 1440px)').matches;
      const overflowLevel = isLargeViewport ? 5 : 4;
      const scrollLocked = (roomsByLevel[overflowLevel]?.length ?? 0) === 0;

      if (scrollLocked) {
        element.scrollLeft = 0;
      }
    },
  );

  resetPendingChild = modifier(
    (_element: Element, [isEditMode]: [boolean]): void => {
      if (!isEditMode) {
        this.editingRoomCode = null;
        this.pendingChildParentCode = null;
      }
    },
  );

  get allUsedRoomCodes(): string[] {
    return this.roomHierarchySettings
      ? collectAllRoomCodes(this.roomHierarchySettings.hierarchy)
      : [];
  }

  /**
   * One column per filled level, with a minimum of 5 columns so there are
   * always empty slots visible beyond the deepest filled level.
   * Falls back to [1–5] when no rooms are assigned yet (default peek layout).
   *
   * Default peek layout (no rooms):
   * 5 columns total.
   * ≥ 1440px: LEVEL 1–4 visible, LEVEL 5th is the peek.
   * < 1440px: LEVEL 1–3 visible, LEVEL 4 is the peek, LEVEL 5th is off-screen.
   */
  get columns(): number[] {
    const levelsWithRooms = this.roomHierarchyByLevel
      .map((rooms, index) => (rooms.length > 0 ? index : null))
      .filter((index): index is number => index !== null);

    const defaultPeekLayout = [1, 2, 3, 4, 5];

    if (levelsWithRooms.length === 0) {
      return defaultPeekLayout;
    }

    const lastLevelWithRooms = levelsWithRooms[levelsWithRooms.length - 1]!;
    const missingColumns = Math.max(0, 5 - levelsWithRooms.length);
    const emptyColumns = Array.from(
      { length: missingColumns },
      (_, i) => lastLevelWithRooms + i + 1,
    );

    return [...levelsWithRooms, ...emptyColumns];
  }

  get defaultRootNode(): RoomOffsetHierarchy {
    return {
      children: {},
      offset: { type: 'absolute', value: 0 },
      ...(this.isMinMaxOffsetEnabled && {
        maximum_offset: { type: 'absolute', value: 0 },
      }),
    };
  }

  /**
   * The production ingestion endpoint doesn't accept `maximum_offset` yet, so
   * every write path must omit it entirely — not just default it to 0 —
   * whenever this toggle is off.
   */
  get isMinMaxOffsetEnabled(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PRICING_MANAGER_CASCADING_MINMAX_ROOM_OFFSETS,
    );
  }

  get leadingRoomCode(): string | null {
    return this.hotelAndMarketService.hotel.pricingIntegration
      .pms_leading_room_pms_id;
  }

  /**
   * Builds a sparse array (1-based index) of rooms per level, following the
   * Miller Columns pattern (https://en.wikipedia.org/wiki/Miller_columns):
   * level 1 always shows all root rooms; each deeper level shows only the
   * children of the room selected at the previous level (via selectionRoomCodePath).
   */
  @cached
  get roomHierarchyByLevel(): roomHierarchyByLevel {
    const hierarchyByLevel: {
      roomCode: string;
      roomHierarchy: RoomOffsetHierarchy;
    }[][] = [];

    if (!this.roomHierarchySettings) {
      return hierarchyByLevel;
    }

    // converts the hierarchy's Record<string, RoomOffsetHierarchy> shape
    // into an array of { roomCode, roomHierarchy } objects.
    const toEntries = (
      hierarchy: Record<string, RoomOffsetHierarchy>,
    ): { roomCode: string; roomHierarchy: RoomOffsetHierarchy }[] =>
      Object.entries(hierarchy).map(([roomCode, roomHierarchy]) => ({
        roomCode,
        roomHierarchy,
      }));

    let currentLevelHierarchy = this.roomHierarchySettings.hierarchy;

    hierarchyByLevel[1] = toEntries(currentLevelHierarchy);

    // Walk the selection path, excluding the first empty entry, for each level that has been clicked
    for (const [level, selectedRoomCode] of this.selectionRoomCodePath
      .slice(1)
      .entries()) {
      const parentNode = currentLevelHierarchy[selectedRoomCode];

      if (!parentNode) {
        break;
      }

      currentLevelHierarchy = parentNode.children;

      // Populates the next level with the children of the current level.
      // In auto-expanded levels (beyond lastActivatedLevel), only the
      // primary-path room is shown — siblings are hidden.
      const levelEntries = toEntries(currentLevelHierarchy);
      const primaryPathRoom =
        level >= this.lastActivatedLevel
          ? this.selectionRoomCodePath[level + 2]
          : undefined;

      hierarchyByLevel[level + 2] = primaryPathRoom
        ? levelEntries.filter((entry) => entry.roomCode === primaryPathRoom)
        : levelEntries;
    }

    // Inject a synthetic placeholder node so the pending-child dropdown renders
    // in the correct column before the user has selected a room code.
    if (this.pendingChildParentCode) {
      const parentLevel = hierarchyByLevel.findIndex((level) =>
        level?.some((room) => room.roomCode === this.pendingChildParentCode),
      );

      if (parentLevel !== -1) {
        const pendingLevel = parentLevel + 1;
        const pendingNode = {
          roomCode: '',
          roomHierarchy: this.defaultRootNode,
        };

        hierarchyByLevel[pendingLevel] = [
          ...(hierarchyByLevel[pendingLevel] ?? []),
          pendingNode,
        ];
      }
    }

    return hierarchyByLevel;
  }

  get roomHierarchySettings(): RoomOffsetHierarchySettings | null {
    const { settings } = this.args.strategy;

    return settings.settings_type === 'room_offset_hierarchy' ? settings : null;
  }

  /**
   * Seeds `selectionRoomCodePath` from the primary path once, on first load.
   * Runs only here (not on every hierarchy edit) so a mutation to
   * `roomHierarchySettings.hierarchy`, a tracked dependency, never
   * silently wipes out the user's manual navigation.
   */
  constructor(
    owner: Owner,
    args: SettingsRecommendationsRoomHierarchyTableRoomHierarchySignature['Args'],
  ) {
    super(owner, args);

    this.selectionRoomCodePath = this.roomHierarchySettings
      ? buildDefaultSelectionPath(
          this.roomHierarchySettings.hierarchy,
          this.leadingRoomCode,
        )
      : [];
  }

  /**
   * Narrows `selectionRoomCodePath` to the clicked room and auto-fills the
   * rest with its own primary sub-path.
   */
  private activateSelectionPath(
    level: number,
    roomCode: string | null,
    children: Record<string, RoomOffsetHierarchy> | undefined,
    leadingRoomCode: string | null,
  ): void {
    if (!roomCode) {
      // Deactivating restores the fully auto-expanded default — recompute
      this.lastActivatedLevel = 0;
      this.selectionRoomCodePath = this.roomHierarchySettings
        ? buildDefaultSelectionPath(
            this.roomHierarchySettings.hierarchy,
            leadingRoomCode,
          )
        : [];
      return;
    }

    // Drop the old branch at/below actual level
    const newPath = this.selectionRoomCodePath.slice(0, level);

    newPath[level] = roomCode;

    if (children) {
      // Auto-fill the branch below with the clicked room's own primary
      // sub-path, so its descendants are immediately auto-expanded too.
      const subPath = findPrimaryPath(children, leadingRoomCode);

      // subPath is 0-indexed from the clicked room's own children;
      // offset it into newPath's global level indices.
      subPath.forEach((code, index) => {
        newPath[level + 1 + index] = code;
      });
    }

    // Activating sets the manual/auto-expand boundary at this level.
    this.lastActivatedLevel = level;
    this.selectionRoomCodePath = newPath;
  }

  /**
   * `selectionRoomCodePath` stores room codes; if the user renames a room
   * that's currently part of the active path, the path would still hold the
   * stale code and the sibling-filter above would stop matching, silently
   * deselecting the room. This swaps old→new in place.
   */
  private renameSelectionPathCode(oldCode: string, newCode: string): void {
    this.selectionRoomCodePath = this.selectionRoomCodePath.map((code) =>
      code === oldCode ? newCode : code,
    );
  }

  @action
  levelHasRooms(level: number): boolean {
    return Boolean(this.roomHierarchyByLevel[level]?.length);
  }

  @action
  onActivate(level: number, roomCode: string | null): void {
    this.selectedRoomCode = roomCode;

    const roomEntry = roomCode
      ? this.roomHierarchyByLevel[level]?.find(
          (room) => room.roomCode === roomCode,
        )
      : undefined;

    this.activateSelectionPath(
      level,
      roomCode,
      roomEntry?.roomHierarchy.children,
      this.leadingRoomCode,
    );
  }

  // Triggered after the user clicks "+ Add child room" to add a new child room.
  @action
  onAddChildRoom(parentRoomCode: string): void {
    // Same parent clicked again while placeholder is still open — do nothing.
    if (this.pendingChildParentCode === parentRoomCode) {
      return;
    }

    // Order matters: pendingChildParentCode invalidates @cached
    // roomHierarchyByLevel and adds a fake node.
    // Find parentLevel first, while the list is still clean.
    const parentLevel = this.roomHierarchyByLevel.findIndex((level) =>
      level?.some((room) => room.roomCode === parentRoomCode),
    );

    // Activate the parent so its children column becomes visible.
    if (parentLevel !== -1) {
      this.onActivate(parentLevel, parentRoomCode);
    }

    this.editingRoomCode = null;
    this.pendingChildParentCode = parentRoomCode;
  }

  // first-ever root creation, or rename/edit of an existing room.
  @action
  onCommitRoom(
    oldRoomCode: string,
    newRoomCode: string,
    minOffsetValue: number | null,
    maxOffsetValue: number | null,
  ): void {
    if (!this.roomHierarchySettings) {
      return;
    }

    if (!oldRoomCode && !newRoomCode) {
      return;
    }

    const settings = this.roomHierarchySettings;

    // Add-child via placeholder: empty oldRoomCode AND a parent is pending.
    if (!oldRoomCode && this.pendingChildParentCode) {
      const parentCode = this.pendingChildParentCode;
      this.args.strategy.settings = {
        ...settings,
        hierarchy: updateHierarchy(settings.hierarchy, (currentRoomCode) => {
          if (currentRoomCode === parentCode) {
            return {
              action: 'add-child',
              childCode: newRoomCode,
              isMinMaxOffsetEnabled: this.isMinMaxOffsetEnabled,
              maxOffsetValue: resolveOffsetValue(maxOffsetValue),
              minOffsetValue: resolveOffsetValue(minOffsetValue),
            };
          }
        }),
      };
      this.editingRoomCode = null;
      this.pendingChildParentCode = null;
      return;
    }

    if (oldRoomCode !== newRoomCode) {
      this.selectedRoomCode = newRoomCode;
      this.renameSelectionPathCode(oldRoomCode, newRoomCode);
    }

    // When oldRoomCode is empty the hierarchy is empty too — pre-seed it with a
    // default root node so updateHierarchy can treat both cases uniformly.
    const baseHierarchy = oldRoomCode
      ? settings.hierarchy
      : { [newRoomCode]: this.defaultRootNode };

    this.editingRoomCode = null;
    this.args.strategy.settings = {
      ...settings,
      hierarchy: updateHierarchy(baseHierarchy, (currentRoomCode, node) => {
        if (currentRoomCode === (oldRoomCode || newRoomCode)) {
          return {
            action: 'update',
            roomCode: newRoomCode,
            node: {
              ...node,
              ...(minOffsetValue !== null && {
                offset: {
                  ...node.offset,
                  value: resolveOffsetValue(minOffsetValue),
                },
                ...(this.isMinMaxOffsetEnabled && {
                  maximum_offset: {
                    type: node.maximum_offset?.type ?? 'absolute',
                    value: resolveOffsetValue(maxOffsetValue),
                  },
                }),
              }),
            },
          };
        }
      }),
    };
  }

  @action
  onRemoveRoom(roomCode: string): void {
    if (!this.roomHierarchySettings) {
      return;
    }
    const settings = this.roomHierarchySettings;

    this.args.strategy.settings = {
      ...settings,
      hierarchy: updateHierarchy(settings.hierarchy, (currentRoomCode) => {
        if (currentRoomCode === roomCode) {
          return { action: 'remove' };
        }
      }),
    };
  }

  @action
  onRoomEdit(roomCode: string | null): void {
    this.editingRoomCode = roomCode;
    this.pendingChildParentCode = null;
  }

  // Single commit path for any row action: add-child via placeholder,

  @action
  parentRoomCodeForLevel(level: number): string | undefined {
    return this.selectionRoomCodePath[level - 1];
  }

  <template>
    <div class={{styles.table-card}} {{this.resetPendingChild @isEditMode}}>
      <PrismCard @contentLayout="noPadding">
        <:header as |Header|>
          <Header.title>
            <div class={{styles.table-header}}>
              <p class="text-300--bold {{styles.section-title}}">{{t
                  "pricing_assistant.recommendations.settings.room_hierarchy.main_hierarchy"
                }}</p>
              <div class={{styles.table-subheader}}>
                <p class="text-300--regular">{{t
                    "pricing_assistant.recommendations.settings.room_hierarchy.least_expensive"
                  }}</p>
                <span class={{styles.arrow-line}}></span>
                <p class="text-300--regular">{{t
                    "pricing_assistant.recommendations.settings.room_hierarchy.most_expensive"
                  }}</p>
              </div>
            </div>
          </Header.title>
        </:header>
        <:content>
          <div
            class={{styles.columns-container}}
            data-has-level4-rooms={{if (this.levelHasRooms 4) "true"}}
            data-has-level5-rooms={{if (this.levelHasRooms 5) "true"}}
            {{this.resetHorizontalScroll this.roomHierarchyByLevel}}
          >
            {{#each this.columns as |column|}}
              <RoomHierarchyColumn
                @activeRoomCode={{this.selectedRoomCode}}
                @editingRoomCode={{this.editingRoomCode}}
                @isEditMode={{@isEditMode}}
                @level={{column}}
                @levelHasRooms={{this.levelHasRooms column}}
                @onActivate={{fn this.onActivate column}}
                @onAddChildRoom={{this.onAddChildRoom}}
                @onCommitRoom={{this.onCommitRoom}}
                @onOffsetRangeErrorChange={{@onOffsetRangeErrorChange}}
                @onRemoveRoom={{this.onRemoveRoom}}
                @onRoomEdit={{this.onRoomEdit}}
                @parentRoomCode={{this.parentRoomCodeForLevel column}}
                @pathRoomCode={{get this.selectionRoomCodePath column}}
                @roomsInLevel={{get this.roomHierarchyByLevel column}}
                @usedRoomCodes={{this.allUsedRoomCodes}}
              />
            {{/each}}
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
